import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '../../../core/models/paginated-response.model';
import { Role } from '../../../core/models/role.model';
import { User } from '../../../core/models/user.model';
import { environment } from '../../../environments/environment';
import { BranchContextService } from '../../../core/services/branch-context.service';
import { UserRoleType } from '../../../core/models/crm-types';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private branchContext = inject(BranchContextService);
  private userApiUrl = `${environment.apiUrl}/users`;
  private roleApiUrl = `${environment.apiUrl}/roles`;

  // Corresponds to: GET /users
  getUsers(
    page: number = 1,
    limit: number = 10,
    filter?: { status?: string; query?: string, role?: UserRoleType },
    sort?: { field: string; direction: 'asc' | 'desc' }
  ): Observable<PaginatedResponse<User>> {
    let params = new HttpParams()
      .set('page', page)
      .set('limit', limit);

    if (filter?.query) {
      params = params.set('search', filter.query);
    }
    if (sort) {
      params = params.set('sortBy', `${sort.field}`);
      params = params.set('sortOrder', `${sort.direction}`);
    }
    if (filter?.status) {
      params = params.set('status', filter.status);
    }

    if (this.branchContext.selectedBranchId()) {
      params = params.set("branchId", this.branchContext.selectedBranchId()!);
    }

    return this.http.get<PaginatedResponse<User>>(this.userApiUrl, { params });
  }

  // Corresponds to: POST /users
  createUser(userData: { email: string; username: string; roleId: string; branchIds: string[] }): Observable<User> {
    return this.http.post<User>(this.userApiUrl, userData);
  }

  // Corresponds to: PATCH /users/:id
  updateUser(id: string, updates: { username?: string; roleId?: string; branchIds?: string[], isDepartmentHead?: boolean }): Observable<User> {
    return this.http.patch<User>(`${this.userApiUrl}/${id}`, updates);
  }

  // Corresponds to: DELETE /users/:id
  deactivateUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.userApiUrl}/${id}`);
  }

  // Corresponds to: PATCH /users/:id/activate
  activateUser(id: string): Observable<void> {
    return this.http.patch<void>(`${this.userApiUrl}/${id}/activate`, {});
  }

  getUserById(id: string): Observable<User> {
    return this.http.get<User>(`${this.userApiUrl}/${id}`);
  }

  // Helper to get roles for the form dropdown
  getRoles(): Observable<PaginatedResponse<Role>> {
    return this.http.get<PaginatedResponse<Role>>(this.roleApiUrl);
  }

  reassignUserData(fromUserId: string, toUserId: string): Observable<any> {
    return this.http.post(`${this.userApiUrl}/reassign-data`, {
      fromUserId,
      toUserId
    });
  }
}